import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  Help as HelpIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { ModernButton } from '../../styles/components/ModernComponents';

interface HelpContent {
  title: string;
  description: string;
  steps?: string[];
  tips?: string[];
  examples?: string[];
  warnings?: string[];
}

interface HelpButtonProps {
  helpContent: HelpContent;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'default';
  tooltipText?: string;
  sx?: any;
}

export default function HelpButton({ 
  helpContent, 
  size = 'medium', 
  color = 'primary',
  tooltipText = 'Help',
  sx = {}
}: HelpButtonProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getIconSize = () => {
    switch (size) {
      case 'small': return 16;
      case 'large': return 24;
      default: return 20;
    }
  };

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <IconButton
          onClick={handleOpen}
          size={size}
          color={color}
          sx={{
            ...sx,
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s ease',
            }
          }}
        >
          <HelpIcon sx={{ fontSize: getIconSize() }} />
        </IconButton>
      </Tooltip>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <HelpIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {helpContent.title}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {helpContent.description}
          </Typography>

          {helpContent.steps && (
            <Box mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                How to Use
              </Typography>
              <List dense>
                {helpContent.steps.map((step, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '50%', 
                          background: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        {index + 1}
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary={step}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {helpContent.tips && (
            <Box mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                💡 Tips
              </Typography>
              <List dense>
                {helpContent.tips.map((tip, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={tip}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {helpContent.examples && (
            <Box mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.main' }}>
                Examples
              </Typography>
              <List dense>
                {helpContent.examples.map((example, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <InfoIcon color="info" sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={example}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { fontStyle: 'italic' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {helpContent.warnings && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                ⚠️ Important Notes
              </Typography>
              <List dense>
                {helpContent.warnings.map((warning, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <WarningIcon color="warning" sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={warning}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <ModernButton onClick={handleClose}>
            Got it
          </ModernButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
